import https from 'https';
import { URL } from 'url';

/**
 * Fetch HTML from Webflow. Uses an HTTPS agent that tolerates local TLS
 * issues (common with corporate proxies / Node CA stores).
 */
export const fetchWebflowHtml = async (
  targetUrl: string,
  hostHeader: string
): Promise<{ ok: boolean; status: number; html: string }> => {
  const parsed = new URL(targetUrl);

  if (parsed.protocol === 'http:') {
    const response = await fetch(targetUrl, {
      method: 'GET',
      headers: {
        Accept: 'text/html,application/xhtml+xml',
        'Accept-Language': 'en',
        'User-Agent': 'Tradewing-Webflow-Hybrid',
        Host: hostHeader,
      },
      redirect: 'follow',
    });

    return {
      ok: response.ok,
      status: response.status,
      html: await response.text(),
    };
  }

  return new Promise((resolve, reject) => {
    const request = https.get(
      targetUrl,
      {
        headers: {
          Accept: 'text/html,application/xhtml+xml',
          'Accept-Language': 'en',
          'User-Agent': 'Tradewing-Webflow-Hybrid',
          Host: hostHeader,
        },
        rejectUnauthorized: false,
        timeout: 15000,
      },
      (response) => {
        const status = response.statusCode || 0;
        const redirectLocation = response.headers.location;

        if (
          status >= 300 &&
          status < 400 &&
          redirectLocation &&
          (response.headers.location as string)
        ) {
          const nextUrl = new URL(redirectLocation, targetUrl).toString();
          fetchWebflowHtml(nextUrl, new URL(nextUrl).host)
            .then(resolve)
            .catch(reject);
          return;
        }

        const chunks: Buffer[] = [];
        response.on('data', (chunk) => chunks.push(Buffer.from(chunk)));
        response.on('end', () => {
          resolve({
            ok: status >= 200 && status < 300,
            status,
            html: Buffer.concat(chunks).toString('utf8'),
          });
        });
      }
    );

    request.on('error', reject);
    request.on('timeout', () => {
      request.destroy();
      reject(new Error(`Webflow fetch timed out: ${targetUrl}`));
    });
  });
};
